import { ComponentProps, FC } from "react";
import { useTranslation } from "react-i18next";

import { Color } from "capitalrx-components-2.0/lib/general";
import { TextColor } from "capitalrx-components-2.0/lib/typography";

import { ThemedDropdown } from "@/App/components/themed/Dropdown";
import { MemberEligibilityMember } from "@/App/types";

import "./DependentDropdown.scss";

export interface DependentRecord {
  member: {
    person_code: string;
    first_name: string;
    last_name: string;
    is_minor_dependent?: boolean;
  };
}

export interface DependentDropdownProps extends Omit<ComponentProps<typeof ThemedDropdown>, "children"> {
  /** the pre-filtered list of minor dependents */
  dependents: DependentRecord[];
  /** current primary member user's details */
  member: MemberEligibilityMember;
  /** maximum width for the wrapper div */
  maxWidth?: string | number;
  showMemberType?: boolean;
  cardholderType?: string;
}

/**
 * Dropdown component that displays the current member plus any minor dependents.
 * Expects minorDependents to be pre-filtered by parent component.
 */
export const DependentDropdown: FC<DependentDropdownProps> = ({
  dependents,
  member,
  showMemberType,
  cardholderType,
  maxWidth = "300px",
  ...dropdownProps
}) => {
  const { t: translate } = useTranslation();

  if (dependents.length === 0) {
    return null;
  }

  if (showMemberType && !cardholderType) {
    cardholderType = translate("DEPENDENT_DROPDOWN.policyholder");
  }

  return (
    <div style={{ maxWidth }}>
      <ThemedDropdown
        {...dropdownProps}
      >
        <ThemedDropdown.Option value={member.person_code} key={member.person_code}>
          {member.first_name} {member.last_name}
          {showMemberType && (
            <TextColor appearance={Color.DarkGrey[400]} className={"member-type"}>&nbsp;({cardholderType})</TextColor>
          )}
        </ThemedDropdown.Option>
        {dependents.map((dependent) => (
          <ThemedDropdown.Option
            value={dependent.member.person_code}
            key={dependent.member.person_code}
          >
            {dependent.member.first_name} {dependent.member.last_name}
            {showMemberType && (
              <TextColor appearance={Color.DarkGrey[400]} className={"member-type"}>&nbsp;({translate("DEPENDENT_DROPDOWN.dependent")})</TextColor>
            )}
          </ThemedDropdown.Option>
        ))}
      </ThemedDropdown>
    </div>
  );
};
